<template>
  <Modal
    :modal-types="[APPLY_CHART_STYLE_TO_CHARTS_MODAL]"
    font-awesome-icon-class="fas fa-fw fa-cogs"
    modal-title="Apply ChartStyle"
  >
    <div class="apply-chartstyle-to-all-container">
      <p>Are you sure you want to apply the settings of the current chart to the saved charts in the selected ChartList?</p>
      <p>
        Please note, this action may also change the Period setting for these charts in the current list and could remove your saved annotations as a result.
        If you would like to prevent this, check the box below.
      </p>
      <SCThreeCheckbox
        id="apply-chart-style-to-charts-modal-ignore-chart-period-checkbox"
        v-model:checkbox-value="shouldIgnoreChartPeriod"
        label-text="Ignore Chart Period"
        :is-label-close-to-icon="true"
        :is-square="true"
        :is-double-spaced="true"
        :is-disabled="isUpdatingChartStyles"
        justify="center"
      />
      <hr class="sc3-form-divider" />
      <button
        type="button"
        class="sc3-button sc3-button-green"
        :disabled="isUpdatingChartStyles"
        @click="updateChartStyles"
      >
        <i v-show="!isUpdatingChartStyles" class="fas fa-fw fa-check-circle"></i>
        <i v-show="isUpdatingChartStyles" class="fad fa-fw fa-spinner-third fa-spin"></i>
        &nbsp;Update Charts
      </button>
      <button
        type="button"
        class="sc3-button-link"
        @click="closeModal()"
      >Close</button>
    </div>
  </Modal>
</template>

<script lang="ts" setup>
  import { ref, watch } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useLayoutStore } from '@/stores/layoutStore';
  import { useActionResultMessages } from '@/composables/useActionResultMessages';
  import { useModals } from '@/composables/useModals';
  import { useApplyChartStyle } from '@/composables/useApplyChartStyle';
  import { getSharpCharts2ChartImageUrlData } from '@/composables/getSharpCharts2ChartImageUrlData';
  import { getStyleUrlSearchParams } from '@/utils/getStyleUrlSearchParams';
  import SCThreeCheckbox from '@/components/checkboxes/SCThreeCheckbox.vue';
  import Modal from '@/components/modals/Modal.vue';

  const layoutStore = useLayoutStore();
  const { modalType } = storeToRefs(layoutStore);
  const {
    addGreenActionResultMessage,
    addYellowActionResultMessage,
    addRedActionResultMessage
  } = useActionResultMessages();
  const { APPLY_CHART_STYLE_TO_CHARTS_MODAL, closeModal } = useModals();
  const { applyChartStyleToCharts } = useApplyChartStyle();
  const isUpdatingChartStyles = ref(false);
  const shouldIgnoreChartPeriod = ref(true);

  function handleModalTypeChange() {
    if (modalType.value !== APPLY_CHART_STYLE_TO_CHARTS_MODAL) {
      return;
    }

    isUpdatingChartStyles.value = false;
    shouldIgnoreChartPeriod.value = true;
  }

  async function updateChartStyles() {
    isUpdatingChartStyles.value = true;
    let styleUrlParameters: URLSearchParams;

    try {
      const sharpCharts2ChartImageUrlData = await getSharpCharts2ChartImageUrlData(true);

      styleUrlParameters = getStyleUrlSearchParams(sharpCharts2ChartImageUrlData);
    } catch (e) {
      window.sc3WorkbenchLogger.error('Failed to get permanent current chart style cache id from updateChartStyles()', e);
      addRedActionResultMessage('Unable to update Charts with ChartStyle.');
      closeModal();
      return;
    }

    const { numFailures, numSuccesses } = await applyChartStyleToCharts(styleUrlParameters, shouldIgnoreChartPeriod.value);

    if (numSuccesses <= 0 && numFailures <= 0) {
      addYellowActionResultMessage('No Charts were updated with ChartStyle.');
    } else if (numFailures <= 0) {
      addGreenActionResultMessage('Success! Charts have been updated with ChartStyle!');
    } else if (numSuccesses <= 0) {
      addRedActionResultMessage('Unable to update Charts with ChartStyle.');
    } else {
      addYellowActionResultMessage('Some Charts were unable to be updated with ChartStyle.');
    }

    closeModal();
  }

  watch(modalType, handleModalTypeChange);
</script>

<style scoped>
  .apply-chartstyle-to-all-container {
    text-align: center;
    padding: 0px 5px;
  }
</style>
